import React, { FC } from "react";
import propTypes from "prop-types";
import {
    FiChevronRight,
    FiHome,
    FiClock,
    FiClipboard
} from 'react-icons/fi';

import { AssetLocation, KeyAuditCriteria, KeyAuditType, KeyManagementTypes } from "../../../../logic/api";

import styles from "./styles";
import flex from "../../../../styles/flex";
import { DateTime } from 'luxon';

import ListViewItemCard from "../../../cards/ListItemCard/ListItemCard";

import { locationPathTextToFriendly } from "../../../../utility/utils";
import { useDate } from "../../../../utility/useDate";
import { useTranslation } from "react-i18next";
import { usePreferences } from "@/utility/usePreferences";
import { ReleaseFlag, useReleaseManagement } from "@/utility/feature-management";
import { subDays } from "date-fns";

const KeyAuditDueListItem: FC<{ item: AssetLocation, onClick?: () => void }> = (props) => {
    const border = styles.border;

    const preferences = usePreferences();
    const { localizeDate, toFormattedDateTimeString } = useDate();
    const { t } = useTranslation();
    const keyAuditSettings = preferences.keyAuditSettings;
    const isEnabledEnforceKeyPositionAudit = useReleaseManagement(ReleaseFlag.releaseKeyPositionAudit6342);
    const isKeyAuditReasonReleased = useReleaseManagement(ReleaseFlag.releaseKeyAuditReason);

    const getAppliedAuditCriteria = (type: KeyManagementTypes) => {
        if ([KeyManagementTypes.box, KeyManagementTypes.hook].includes(type)) {
            return keyAuditSettings?.lane
        } else {
            return keyAuditSettings?.standalone
        }
    }

    const getAuditType = (item: AssetLocation) => {
        const locationKeyAuditPreferences = getAppliedAuditCriteria(item.keyType);

        return (isEnabledEnforceKeyPositionAudit && [KeyManagementTypes.hook, KeyManagementTypes.box].includes(item.keyType))
            && locationKeyAuditPreferences?.auditType === KeyAuditType.Order
            ? t("settings.keyAudits.type.Order")
            : t("settings.keyAudits.type.Contents")
    }

    const getAuditCriteriaText = (item: AssetLocation) => {
        if (item.keyAuditReason === KeyAuditCriteria.LaneAuditComplete) {
            return t("settings.keyAudits.criteriaInfo.LaneAuditComplete", { locationName: item.name });
        }

        if (item.keyAuditReason === KeyAuditCriteria.FullBox) {
            return t("settings.keyAudits.criteriaInfo.FullBox");
        }

        if (item.keyAuditReason === KeyAuditCriteria["24HoursNoMovement"]) {
            return t("settings.keyAudits.criteriaInfo.24HoursNoMovement");
        }

        if (item.keyAuditReason === KeyAuditCriteria.AnyMovement) {
            return t("settings.keyAudits.criteriaInfo.AnyMovement");
        }
    }

    const getAuditCriteriaDetail = (item: AssetLocation) => {
        if (item.keyAuditReason === KeyAuditCriteria.LaneAuditComplete) {
            return toFormattedDateTimeString(localizeDate(item.auditDate).toISO()?.toString());
        }

        if (item.keyAuditReason === KeyAuditCriteria.FullBox) {
            return `${item.keyCount?.toString()}/${item.capacity?.toString()}`
        }

        if (item.keyAuditReason === KeyAuditCriteria["24HoursNoMovement"]) {
            return toFormattedDateTimeString(subDays(localizeDate(item.keyAuditDueDate).toJSDate(), 1).toISOString());
        }

        if (item.keyAuditReason === KeyAuditCriteria.AnyMovement) {
            return toFormattedDateTimeString(localizeDate(item.keyAuditDueDate).toISO()?.toString());
        }
    }

    return <div style={{ ...styles.listItem, ...border }} onClick={() => {
        if (props.onClick != null) {
            props.onClick();
        }
    }}>
        <div style={{ position: "absolute", textAlign: "center", width: 120, left: -67, bottom: 0, height: 15, top: 30, fontSize: 10, overflow: "hidden", textOverflow: "ellipsis", color: "white", transform: "rotate(270deg)" }}>
            <span>{t("lists.auditDue")}</span>
        </div>
        <div style={styles.listItemFlex}>
            <ListViewItemCard
                icon={<FiClock style={styles.icon} />}
                marginTop={2}
                subText={toFormattedDateTimeString(new Date(props.item.keyAuditDueDate))}
                text={t("locations.due")}
            />

            <ListViewItemCard
                icon={<FiHome style={styles.icon} />}
                marginTop={2}
                text={props.item.name}
                subText={locationPathTextToFriendly(props.item.pathText)}
            />

            {isKeyAuditReasonReleased && props.item.keyAuditReason != null && <ListViewItemCard
                icon={<FiClipboard style={styles.icon} />}
                marginTop={2}
                text={getAuditType(props.item)}
                subText={getAuditCriteriaText(props.item)}
                subSubText={getAuditCriteriaDetail(props.item)}
            />}

            <div style={flex.flexAuto}>
                <div style={{ ...flex.flexRows, alignItems: "center", height: "100%" }}>
                    <div style={flex.flexAuto}>
                        <FiChevronRight style={styles.iconChevron} />
                    </div>
                </div>
            </div>
        </div>
    </div>
}


KeyAuditDueListItem.propTypes = {
    item: propTypes.any.isRequired,
    onClick: propTypes.func
};


export default KeyAuditDueListItem;
